import React, { useEffect, useMemo, useRef, useState } from "react";
import * as XLSX from "xlsx";
import {
  addDoc,
  collection,
  deleteDoc,
  doc,
  getDocs,
  onSnapshot,
  orderBy,
  query,
  serverTimestamp,
  setDoc,
  updateDoc,
  where,
  writeBatch,
} from "firebase/firestore";
import {
  FaBullhorn,
  FaCloudUploadAlt,
  FaDownload,
  FaEdit,
  FaFileAlt,
  FaLayerGroup,
  FaPlus,
  FaSearch,
  FaTrashAlt,
  FaUserPlus,
  FaUsers,
  FaWhatsapp,
} from "react-icons/fa";
import { FiRefreshCcw } from "react-icons/fi";
import { db } from "../firebase";
import { downloadCsv } from "../utils/csv";
import {
  buildBodyVariableDefaults,
  buildSampleContactRows,
  cleanText,
  CRM_APPROVED_DEFAULT_TEMPLATES,
  mapVariablePairsToObject,
  normalizeImportedContacts,
  toCrmContactPayload,
} from "../utils/crm";
import { sendCrmWhatsappBroadcast } from "../services/crmAdminService";
import InviteAppUsers from "./invite-app-users";
import "./crm.css";

const TABS = [
  { key: "contacts", label: "Contacts", icon: <FaUsers /> },
  { key: "broadcast", label: "WhatsApp Broadcast", icon: <FaWhatsapp /> },
  { key: "reports", label: "Broadcast Report", icon: <FaBullhorn /> },
  { key: "invite-app-users", label: "Invite app-users", icon: <FaUserPlus /> },
];

const EMPTY_CONTACT_FORM = {
  fullName: "",
  name: "",
  phone: "",
  email: "",
  city: "",
  groupName: "",
};

function safeEmployee() {
  try {
    return JSON.parse(localStorage.getItem("employee") || "{}");
  } catch {
    return {};
  }
}

export default function CrmManager() {
  const employee = useMemo(() => safeEmployee(), []);
  const [activeTab, setActiveTab] = useState("contacts");
  const [contacts, setContacts] = useState([]);
  const [groups, setGroups] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [broadcasts, setBroadcasts] = useState([]);
  const [selectedContactIds, setSelectedContactIds] = useState([]);
  const [contactForm, setContactForm] = useState(EMPTY_CONTACT_FORM);
  const [editingContactId, setEditingContactId] = useState("");
  const [groupDraft, setGroupDraft] = useState("");
  const [bulkGroupName, setBulkGroupName] = useState("");
  const [contactFilters, setContactFilters] = useState({
    search: "",
    city: "all",
    group: "all",
  });
  const [importState, setImportState] = useState({
    fileName: "",
    accepted: [],
    rejected: [],
    assignedGroup: "",
  });
  const [notice, setNotice] = useState({ type: "", text: "" });
  const [broadcastForm, setBroadcastForm] = useState({
    title: "",
    sourceType: "group",
    groupName: "",
    individualContactId: "",
    templateId: "",
  });
  const [bodyVarPairs, setBodyVarPairs] = useState([]);
  const [progressModal, setProgressModal] = useState({
    open: false,
    broadcastId: "",
    data: null,
  });
  const [viewingBroadcast, setViewingBroadcast] = useState(null);
  const [recipientRows, setRecipientRows] = useState([]);
  const [busy, setBusy] = useState("");
  const noticeTimerRef = useRef(null);

  useEffect(() => () => {
    if (noticeTimerRef.current) {
      clearTimeout(noticeTimerRef.current);
    }
  }, []);

  useEffect(() => {
    const unsubContacts = onSnapshot(query(collection(db, "crm_data"), orderBy("createdAt", "desc")), (snap) => {
      const rows = snap.docs.map((item) => ({ id: item.id, ...item.data() }));
      setContacts(rows);
    });

    const unsubGroups = onSnapshot(query(collection(db, "crm_groups"), orderBy("name", "asc")), (snap) => {
      const rows = snap.docs.map((item) => ({ id: item.id, ...item.data() }));
      setGroups(rows);
    });

    const unsubTemplates = onSnapshot(query(collection(db, "crm_whatsapp_templates"), orderBy("templateName", "asc")), (snap) => {
      const rows = snap.docs.map((item) => ({ id: item.id, ...item.data() }));
      setTemplates(rows);
    });

    const unsubBroadcasts = onSnapshot(query(collection(db, "crm_broadcasts"), orderBy("createdAt", "desc")), (snap) => {
      const rows = snap.docs.map((item) => ({ id: item.id, ...item.data() }));
      setBroadcasts(rows);
    });

    return () => {
      unsubContacts();
      unsubGroups();
      unsubTemplates();
      unsubBroadcasts();
    };
  }, []);

  useEffect(() => {
    if (!progressModal.open || !progressModal.broadcastId) return undefined;

    const unsub = onSnapshot(doc(db, "crm_broadcasts", progressModal.broadcastId), (snap) => {
      if (!snap.exists()) return;
      setProgressModal((prev) => ({
        ...prev,
        data: { id: snap.id, ...snap.data() },
      }));
    });
    return () => unsub();
  }, [progressModal.broadcastId, progressModal.open]);

  useEffect(() => {
    if (!viewingBroadcast?.id) {
      setRecipientRows([]);
      return undefined;
    }
    const unsub = onSnapshot(collection(db, "crm_broadcasts", viewingBroadcast.id, "recipients"), (snap) => {
      setRecipientRows(snap.docs.map((item) => ({ id: item.id, ...item.data() })));
    });
    return () => unsub();
  }, [viewingBroadcast?.id]);

  const totals = useMemo(() => ({
    contacts: contacts.length,
    groups: groups.length,
    templates: templates.length,
    broadcasts: broadcasts.length,
  }), [broadcasts.length, contacts.length, groups.length, templates.length]);

  const contactCities = useMemo(
    () => ["all", ...Array.from(new Set(contacts.map((item) => cleanText(item.city)).filter(Boolean))).sort((a, b) => a.localeCompare(b))],
    [contacts]
  );
  const groupNames = useMemo(
    () => ["all", ...Array.from(new Set(groups.map((item) => cleanText(item.name)).filter(Boolean))).sort((a, b) => a.localeCompare(b))],
    [groups]
  );

  const filteredContacts = useMemo(() => {
    const search = contactFilters.search.trim().toLowerCase();
    return contacts.filter((item) => {
      if (contactFilters.city !== "all" && cleanText(item.city) !== contactFilters.city) {
        return false;
      }
      if (contactFilters.group !== "all" && cleanText(item.groupName) !== contactFilters.group) {
        return false;
      }
      if (!search) return true;
      const haystack = [
        item.fullName,
        item.name,
        item.phoneE164,
        item.phoneRaw,
        item.email,
        item.city,
        item.groupName,
      ].join(" ").toLowerCase();
      return haystack.includes(search);
    });
  }, [contactFilters.city, contactFilters.group, contactFilters.search, contacts]);

  const allTemplates = useMemo(() => {
    const merged = [...CRM_APPROVED_DEFAULT_TEMPLATES, ...templates];
    const seen = new Set();
    return merged.filter((item) => {
      const key = `${item.wid}__${item.templateName}`;
      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    });
  }, [templates]);

  const templateOptions = useMemo(
    () => allTemplates.map((item) => ({ id: item.id, label: `${item.templateName || "Untitled"} | ${item.wid || "-"}` })),
    [allTemplates]
  );

  const contactOptions = useMemo(
    () => contacts.map((item) => ({
      id: item.id,
      label: `${item.fullName || item.name || "Unnamed"} | ${item.phoneE164 || item.phoneRaw || "-"}`,
    })),
    [contacts]
  );

  const selectedTemplate = useMemo(
    () => allTemplates.find((item) => item.id === broadcastForm.templateId) || null,
    [allTemplates, broadcastForm.templateId]
  );

  const selectedIndividualContact = useMemo(
    () => contacts.find((item) => item.id === broadcastForm.individualContactId) || null,
    [broadcastForm.individualContactId, contacts]
  );

  useEffect(() => {
    setBodyVarPairs(buildBodyVariableDefaults(selectedTemplate?.bodyVariableCount || 0));
  }, [selectedTemplate?.bodyVariableCount, selectedTemplate?.id]);

  const progressPercent = useMemo(() => {
    const total = Number(progressModal.data?.total || 0);
    if (!total) return 0;
    const sent = Number(progressModal.data?.sent || 0);
    const failed = Number(progressModal.data?.failed || 0);
    return Math.min(100, Math.round(((sent + failed) / total) * 100));
  }, [progressModal.data?.failed, progressModal.data?.sent, progressModal.data?.total]);

  const canCloseProgress = useMemo(() => {
    const status = cleanText(progressModal.data?.status || "");
    return ["completed", "failed", "partial"].includes(status);
  }, [progressModal.data?.status]);

  function showNotice(text, type = "ok") {
    if (noticeTimerRef.current) clearTimeout(noticeTimerRef.current);
    setNotice({ text, type });
    noticeTimerRef.current = setTimeout(() => setNotice({ text: "", type: "" }), 3200);
  }

  function resetContactForm() {
    setContactForm(EMPTY_CONTACT_FORM);
    setEditingContactId("");
  }

  async function refreshGroupCounts(names = []) {
    const uniqueNames = Array.from(new Set(names.map((item) => cleanText(item)).filter(Boolean)));
    await Promise.all(uniqueNames.map(async (name) => {
      const snap = await getDocs(query(collection(db, "crm_data"), where("groupName", "==", name)));
      const target = groups.find((item) => cleanText(item.name) === name);
      if (target?.id) {
        await updateDoc(doc(db, "crm_groups", target.id), {
          contactCount: snap.size,
          updatedAt: serverTimestamp(),
        });
      }
    }));
  }

  async function handleCreateGroup() {
    const name = cleanText(groupDraft, 80);
    if (!name) {
      showNotice("Group name is required.", "err");
      return;
    }
    const existing = groups.find((item) => cleanText(item.name).toLowerCase() === name.toLowerCase());
    if (existing) {
      showNotice("That group already exists.", "err");
      return;
    }

    setBusy("group");
    try {
      await addDoc(collection(db, "crm_groups"), {
        name,
        contactCount: 0,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      });
      setGroupDraft("");
      showNotice("CRM group created.");
    } catch (error) {
      showNotice(error?.message || "Unable to create group.", "err");
    } finally {
      setBusy("");
    }
  }

  async function handleSampleDownload() {
    const rows = buildSampleContactRows();
    const sheet = XLSX.utils.json_to_sheet(rows);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, sheet, "CRM Contacts");
    XLSX.writeFile(wb, "crm-contact-sample.xlsx");
  }

  async function handleImportFile(event) {
    const file = event.target.files?.[0];
    if (!file) return;

    try {
      const buffer = await file.arrayBuffer();
      const workbook = XLSX.read(buffer, { type: "array" });
      const firstSheet = workbook.SheetNames[0];
      const rows = XLSX.utils.sheet_to_json(workbook.Sheets[firstSheet], { defval: "" });
      const normalized = normalizeImportedContacts(rows, {
        source: "bulk_upload",
        groupName: importState.assignedGroup,
      });
      setImportState((prev) => ({
        ...prev,
        fileName: file.name,
        accepted: normalized.accepted,
        rejected: normalized.rejected,
      }));
      showNotice(`Parsed ${normalized.accepted.length} valid contacts.`);
    } catch (error) {
      showNotice(error?.message || "Unable to parse file.", "err");
    } finally {
      event.target.value = "";
    }
  }

  async function handleSaveImportedContacts() {
    if (!importState.accepted.length) {
      showNotice("Upload a valid CSV or Excel file first.", "err");
      return;
    }

    setBusy("saveImport");
    try {
      const batch = writeBatch(db);
      const touchedGroups = new Set();
      importState.accepted.forEach((row) => {
        const ref = doc(collection(db, "crm_data"));
        const groupName = cleanText(importState.assignedGroup || row.groupName || "");
        batch.set(ref, {
          fullName: row.fullName,
          name: row.name,
          phoneRaw: row.phoneRaw,
          phoneE164: row.phoneE164,
          phoneNational: row.phoneNational,
          countryCode: row.countryCode,
          email: row.email,
          city: row.city,
          groupName,
          source: "bulk_upload",
          createdAt: serverTimestamp(),
          updatedAt: serverTimestamp(),
          createdBy: {
            docId: cleanText(employee?.docId || ""),
            name: cleanText(employee?.name || employee?.fullName || ""),
          },
        });
        if (groupName) touchedGroups.add(groupName);
      });
      await batch.commit();
      await refreshGroupCounts(Array.from(touchedGroups));
      setImportState({
        fileName: "",
        accepted: [],
        rejected: [],
        assignedGroup: "",
      });
      showNotice("Contacts saved to crm_data.");
    } catch (error) {
      showNotice(error?.message || "Unable to save contacts.", "err");
    } finally {
      setBusy("");
    }
  }

  async function handleSaveContact(event) {
    event.preventDefault();
    const built = toCrmContactPayload(contactForm, {
      source: editingContactId ? "manual" : "manual",
    });
    if (!built.ok) {
      showNotice(built.error, "err");
      return;
    }

    setBusy("contact");
    try {
      const payload = {
        ...built.payload,
        updatedAt: serverTimestamp(),
        createdBy: {
          docId: cleanText(employee?.docId || ""),
          name: cleanText(employee?.name || employee?.fullName || ""),
        },
      };

      if (editingContactId) {
        const previous = contacts.find((item) => item.id === editingContactId);
        await updateDoc(doc(db, "crm_data", editingContactId), payload);
        await refreshGroupCounts([previous?.groupName, payload.groupName]);
        showNotice("Contact updated.");
      } else {
        await addDoc(collection(db, "crm_data"), {
          ...payload,
          createdAt: serverTimestamp(),
        });
        await refreshGroupCounts([payload.groupName]);
        showNotice("Contact added.");
      }
      resetContactForm();
    } catch (error) {
      showNotice(error?.message || "Unable to save contact.", "err");
    } finally {
      setBusy("");
    }
  }

  function startEditContact(contact) {
    setEditingContactId(contact.id);
    setContactForm({
      fullName: contact.fullName || "",
      name: contact.name || "",
      phone: contact.phoneE164 || contact.phoneRaw || "",
      email: contact.email || "",
      city: contact.city || "",
      groupName: contact.groupName || "",
    });
    setActiveTab("contacts");
  }

  async function handleDeleteContact(contact) {
    const confirmed = window.confirm(`Delete ${contact.fullName || contact.name || "this contact"}?`);
    if (!confirmed) return;

    setBusy(`delete-${contact.id}`);
    try {
      await deleteDoc(doc(db, "crm_data", contact.id));
      await refreshGroupCounts([contact.groupName]);
      setSelectedContactIds((prev) => prev.filter((item) => item !== contact.id));
      showNotice("Contact deleted.");
    } catch (error) {
      showNotice(error?.message || "Unable to delete contact.", "err");
    } finally {
      setBusy("");
    }
  }

  async function handleAssignSelectedGroup() {
    const name = cleanText(bulkGroupName);
    if (!selectedContactIds.length || !name) {
      showNotice("Select contacts and choose a group first.", "err");
      return;
    }

    setBusy("assignGroup");
    try {
      const batch = writeBatch(db);
      const touchedGroups = new Set([name]);
      selectedContactIds.forEach((id) => {
        const current = contacts.find((item) => item.id === id);
        if (current?.groupName) touchedGroups.add(current.groupName);
        batch.update(doc(db, "crm_data", id), {
          groupName: name,
          updatedAt: serverTimestamp(),
        });
      });
      await batch.commit();
      await refreshGroupCounts(Array.from(touchedGroups));
      showNotice("Selected contacts assigned to group.");
    } catch (error) {
      showNotice(error?.message || "Unable to assign group.", "err");
    } finally {
      setBusy("");
    }
  }

  async function handleStartBroadcast() {
    if (!selectedTemplate) {
      showNotice("Select a local WhatsApp template first.", "err");
      return;
    }

    if (broadcastForm.sourceType === "group" && !cleanText(broadcastForm.groupName)) {
      showNotice("Choose a contact group.", "err");
      return;
    }

    if (broadcastForm.sourceType === "individual" && !cleanText(broadcastForm.individualContactId)) {
      showNotice("Choose one saved contact.", "err");
      return;
    }

    setBusy("broadcastStart");
    try {
      const broadcastRef = doc(collection(db, "crm_broadcasts"));
      await setDoc(broadcastRef, {
        title: cleanText(broadcastForm.title || selectedTemplate.templateName || "CRM Broadcast", 120),
        sourceType: broadcastForm.sourceType,
        groupName: cleanText(broadcastForm.groupName || ""),
        templateWid: cleanText(selectedTemplate.wid || ""),
        templateName: cleanText(selectedTemplate.templateName || ""),
        templateCategory: cleanText(selectedTemplate.category || ""),
        type: cleanText(selectedTemplate.type || "text"),
        total: 0,
        sent: 0,
        failed: 0,
        pending: 0,
        status: "running",
        provider: "authkey",
        deliveryMode: "api_status_only",
        createdAt: serverTimestamp(),
        completedAt: null,
        createdBy: {
          docId: cleanText(employee?.docId || ""),
          name: cleanText(employee?.name || employee?.fullName || ""),
        },
        currentNumber: "",
        errorList: [],
      });

      setProgressModal({
        open: true,
        broadcastId: broadcastRef.id,
        data: {
          id: broadcastRef.id,
          status: "running",
          title: cleanText(broadcastForm.title || selectedTemplate.templateName || "CRM Broadcast", 120),
          total: 0,
          sent: 0,
          failed: 0,
          pending: 0,
          currentNumber: "",
          errorList: [],
        },
      });

      const res = await sendCrmWhatsappBroadcast({
        broadcastId: broadcastRef.id,
        title: cleanText(broadcastForm.title || selectedTemplate.templateName || "CRM Broadcast", 120),
        sourceType: broadcastForm.sourceType,
        groupName: cleanText(broadcastForm.groupName || ""),
        individualContactId: cleanText(broadcastForm.individualContactId || ""),
        templateId: selectedTemplate.id,
        templateMeta: {
          wid: selectedTemplate.wid,
          templateName: selectedTemplate.templateName,
          category: selectedTemplate.category,
          type: selectedTemplate.type,
          language: selectedTemplate.language,
          bodyVariableCount: selectedTemplate.bodyVariableCount,
          headerRequired: selectedTemplate.headerRequired,
          headerType: selectedTemplate.headerType,
          hasButton: selectedTemplate.hasButton,
          buttonType: selectedTemplate.buttonType,
          source: selectedTemplate.source || "firestore",
          headerData: selectedTemplate.headerData || "",
          headerFileName: selectedTemplate.headerFileName || "",
        },
        bodyValues: mapVariablePairsToObject(bodyVarPairs),
        headerValues: selectedTemplate.type === "media" ? {
          headerData: cleanText(selectedTemplate.headerData || "", 500),
          headerFileName: cleanText(selectedTemplate.headerFileName || "", 120),
        } : {},
      });

      if (res && res.success === false) {
        showNotice(res.message || "Unable to start broadcast.", "err");
        setProgressModal((prev) => ({
          ...prev,
          open: false,
        }));
        return;
      }

      showNotice(`Broadcast finished with status: ${res.status || "completed"}.`);
    } catch (error) {
      showNotice(error?.message || "Unable to start broadcast.", "err");
      setProgressModal((prev) => ({
        ...prev,
        open: false,
      }));
    } finally {
      setBusy("");
    }
  }

  function exportRecipientsCsv() {
    if (!viewingBroadcast) return;
    downloadCsv(`crm-broadcast-${viewingBroadcast.id}.csv`, recipientRows);
  }

  const allFilteredSelected = filteredContacts.length > 0 && filteredContacts.every((item) => selectedContactIds.includes(item.id));

  return (
    <div className="crm-page">
      <section className="crm-hero">
        <p className="crm-kicker">CRM Control</p>
        <h1 className="crm-title">Manage CRM</h1>
        <p className="crm-subtitle">
          Upload contacts, organize groups, maintain approved-local template metadata, and run server-side Authkey broadcasts with Firestore-backed progress reporting.
        </p>
        <div className="crm-hero-stats">
          <StatCard label="Contacts" value={totals.contacts} />
          <StatCard label="Groups" value={totals.groups} />
          <StatCard label="Templates" value={totals.templates} />
          <StatCard label="Campaigns" value={totals.broadcasts} />
        </div>
      </section>

      {notice.text ? (
        <div className={`crm-badge ${notice.type === "err" ? "danger" : "ok"}`}>{notice.text}</div>
      ) : null}

      <section className="crm-section-card">
        <div className="crm-tabs">
          {TABS.map((tab) => (
            <button
              key={tab.key}
              type="button"
              className={`crm-tab ${activeTab === tab.key ? "active" : ""}`}
              onClick={() => setActiveTab(tab.key)}
            >
              {tab.icon} {tab.label}
            </button>
          ))}
        </div>

        <div className="crm-section-body">
          {activeTab === "contacts" ? (
            <>
              <div className="crm-two-col crm-two-col-compact">
                <div className="crm-panel">
                  <h3>{editingContactId ? "Edit Contact" : "Add Single Contact"}</h3>
                  <form onSubmit={handleSaveContact} className="crm-grid">
                    <Field label="Full Name">
                      <input value={contactForm.fullName} onChange={(event) => setContactForm((prev) => ({ ...prev, fullName: event.target.value }))} />
                    </Field>
                    <Field label="Display Name">
                      <input value={contactForm.name} onChange={(event) => setContactForm((prev) => ({ ...prev, name: event.target.value }))} />
                    </Field>
                    <Field label="Phone">
                      <input value={contactForm.phone} onChange={(event) => setContactForm((prev) => ({ ...prev, phone: event.target.value }))} placeholder="7002618625 / +917002618625 / 917002618625" />
                    </Field>
                    <Field label="Email">
                      <input value={contactForm.email} onChange={(event) => setContactForm((prev) => ({ ...prev, email: event.target.value }))} />
                    </Field>
                    <Field label="City">
                      <input value={contactForm.city} onChange={(event) => setContactForm((prev) => ({ ...prev, city: event.target.value }))} />
                    </Field>
                    <Field label="Group">
                      <input value={contactForm.groupName} onChange={(event) => setContactForm((prev) => ({ ...prev, groupName: event.target.value }))} />
                    </Field>
                    <div className="crm-actions">
                      <button type="submit" className="crm-btn" disabled={busy === "contact"}>
                        <FaPlus /> {busy === "contact" ? "Saving..." : editingContactId ? "Update Contact" : "Add Contact"}
                      </button>
                      {editingContactId ? (
                        <button type="button" className="crm-btn-secondary" onClick={resetContactForm}>Cancel</button>
                      ) : null}
                    </div>
                  </form>

                  <div className="crm-panel" style={{ marginTop: 14, padding: 14 }}>
                    <h4>Create Group</h4>
                    <div className="crm-inline-actions">
                      <input
                        style={{ flex: 1 }}
                        value={groupDraft}
                        onChange={(event) => setGroupDraft(event.target.value)}
                        placeholder="Warm Leads"
                      />
                      <button type="button" className="crm-btn-secondary" onClick={handleCreateGroup} disabled={busy === "group"}>
                        <FaLayerGroup /> {busy === "group" ? "Creating..." : "Create Group"}
                      </button>
                    </div>
                    <div className="crm-chip-row" style={{ marginTop: 12 }}>
                      {groups.map((item) => (
                        <span key={item.id} className="crm-chip">
                          {item.name} • {item.contactCount || 0}
                        </span>
                      ))}
                    </div>
                  </div>
                </div>
              </div>

              <div className="crm-panel crm-panel-wide">
                <h3>Upload Excel or CSV</h3>
                <p className="crm-panel-sub">
                  Accepted fields: <strong>fullName</strong>, <strong>name</strong>, <strong>phone</strong>, <strong>email</strong>, <strong>city</strong>, <strong>groupName</strong>.
                </p>
                <div className="crm-grid crm-grid-wide">
                  <div className="crm-field">
                    <label>Assign uploaded contacts to group</label>
                    <select
                      value={importState.assignedGroup}
                      onChange={(event) => setImportState((prev) => ({ ...prev, assignedGroup: event.target.value }))}
                    >
                      <option value="">Keep imported value</option>
                      {groups.map((item) => <option key={item.id} value={item.name}>{item.name}</option>)}
                    </select>
                  </div>
                  <div className="crm-field">
                    <label>Upload file</label>
                    <input type="file" accept=".csv,.xlsx,.xls" onChange={handleImportFile} />
                  </div>
                </div>
                <div className="crm-actions" style={{ marginTop: 14 }}>
                  <button type="button" className="crm-btn-secondary" onClick={handleSampleDownload}>
                    <FaDownload /> Download sample Excel
                  </button>
                  <button type="button" className="crm-btn-secondary" onClick={() => downloadCsv("crm-contact-sample.csv", buildSampleContactRows())}>
                    <FaFileAlt /> Download sample CSV
                  </button>
                  <button type="button" className="crm-btn" onClick={handleSaveImportedContacts} disabled={busy === "saveImport"}>
                    <FaCloudUploadAlt /> {busy === "saveImport" ? "Saving..." : "Save Contacts"}
                  </button>
                </div>

                <div style={{ marginTop: 18 }}>
                  <div className="crm-chip-row">
                    <span className="crm-chip">File: {importState.fileName || "No file yet"}</span>
                    <span className="crm-chip">Valid rows: {importState.accepted.length}</span>
                    <span className="crm-chip">Rejected rows: {importState.rejected.length}</span>
                  </div>
                  <div className="crm-table-wrap" style={{ marginTop: 14 }}>
                    <table className="crm-table" style={{ minWidth: 760 }}>
                      <thead>
                        <tr>
                          <th>Name</th>
                          <th>Phone</th>
                          <th>Email</th>
                          <th>City</th>
                          <th>Group</th>
                        </tr>
                      </thead>
                      <tbody>
                        {importState.accepted.length ? importState.accepted.map((row) => (
                          <tr key={row.previewKey}>
                            <td>{row.fullName}<small>{row.name}</small></td>
                            <td>{row.phoneE164}</td>
                            <td>{row.email || "—"}</td>
                            <td>{row.city || "—"}</td>
                            <td>{importState.assignedGroup || row.groupName || "—"}</td>
                          </tr>
                        )) : (
                          <tr><td colSpan={5}>Upload a file to preview data before saving.</td></tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                  {importState.rejected.length ? (
                    <div className="crm-errors" style={{ marginTop: 12 }}>
                      {importState.rejected.map((item) => (
                        <div key={`${item.index}-${item.error}`}>Row {item.index}: {item.error}</div>
                      ))}
                    </div>
                  ) : null}
                </div>
              </div>

              <div className="crm-panel">
                <div className="crm-toolbar">
                  <div>
                    <h3 style={{ marginBottom: 6 }}>Contact Directory</h3>
                    <p className="crm-panel-sub" style={{ marginBottom: 0 }}>
                      Filter by group, city, or free-text search across name, phone, and email.
                    </p>
                  </div>
                  <div className="crm-inline-actions">
                    <select value={bulkGroupName} onChange={(event) => setBulkGroupName(event.target.value)}>
                      <option value="">Assign selected to group</option>
                      {groups.map((item) => <option key={item.id} value={item.name}>{item.name}</option>)}
                    </select>
                    <button type="button" className="crm-btn-secondary" onClick={handleAssignSelectedGroup} disabled={busy === "assignGroup"}>
                      <FaLayerGroup /> {busy === "assignGroup" ? "Assigning..." : "Assign Group"}
                    </button>
                    <button
                      type="button"
                      className="crm-btn-secondary"
                      onClick={() => setSelectedContactIds(allFilteredSelected ? [] : filteredContacts.map((item) => item.id))}
                    >
                      {allFilteredSelected ? "Clear Selection" : "Select All Filtered"}
                    </button>
                  </div>
                </div>

                <div className="crm-filters" style={{ marginTop: 14 }}>
                  <input
                    value={contactFilters.search}
                    onChange={(event) => setContactFilters((prev) => ({ ...prev, search: event.target.value }))}
                    placeholder="Search name, phone, email"
                  />
                  <select value={contactFilters.group} onChange={(event) => setContactFilters((prev) => ({ ...prev, group: event.target.value }))}>
                    {groupNames.map((item) => <option key={item} value={item}>{item === "all" ? "All groups" : item}</option>)}
                  </select>
                  <select value={contactFilters.city} onChange={(event) => setContactFilters((prev) => ({ ...prev, city: event.target.value }))}>
                    {contactCities.map((item) => <option key={item} value={item}>{item === "all" ? "All cities" : item}</option>)}
                  </select>
                  <span className="crm-chip"><FaSearch /> {filteredContacts.length} results</span>
                </div>

                <div className="crm-table-wrap" style={{ marginTop: 14 }}>
                  <table className="crm-table">
                    <thead>
                      <tr>
                        <th><input type="checkbox" checked={allFilteredSelected} onChange={() => setSelectedContactIds(allFilteredSelected ? [] : filteredContacts.map((item) => item.id))} /></th>
                        <th>Contact</th>
                        <th>Phone</th>
                        <th>Email</th>
                        <th>City</th>
                        <th>Group</th>
                        <th>Source</th>
                        <th>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredContacts.length ? filteredContacts.map((item) => (
                        <tr key={item.id}>
                          <td>
                            <input
                              type="checkbox"
                              checked={selectedContactIds.includes(item.id)}
                              onChange={() => setSelectedContactIds((prev) => (
                                prev.includes(item.id)
                                  ? prev.filter((entry) => entry !== item.id)
                                  : [...prev, item.id]
                              ))}
                            />
                          </td>
                          <td>{item.fullName || item.name}<small>{item.name || "—"}</small></td>
                          <td>{item.phoneE164}<small>{item.phoneRaw || "—"}</small></td>
                          <td>{item.email || "—"}</td>
                          <td>{item.city || "—"}</td>
                          <td>{item.groupName || "—"}</td>
                          <td><span className={`crm-badge ${item.source === "bulk_upload" ? "info" : "warn"}`}>{item.source || "manual"}</span></td>
                          <td>
                            <div className="crm-inline-actions">
                              <button type="button" className="crm-btn-secondary" onClick={() => startEditContact(item)}>
                                <FaEdit />
                              </button>
                              <button type="button" className="crm-btn-danger" onClick={() => handleDeleteContact(item)} disabled={busy === `delete-${item.id}`}>
                                <FaTrashAlt />
                              </button>
                            </div>
                          </td>
                        </tr>
                      )) : (
                        <tr><td colSpan={8}>No contacts match the current filters.</td></tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            </>
          ) : null}

          {activeTab === "broadcast" ? (
            <div className="crm-two-col">
              <div className="crm-panel">
                <h3>WhatsApp Broadcast Builder</h3>
                <p className="crm-panel-sub">
                  Choose saved contact or saved group, select the approved template, then send immediately.
                </p>

                <div className="crm-grid">
                  <Field label="Campaign title">
                    <input
                      value={broadcastForm.title}
                      onChange={(event) => setBroadcastForm((prev) => ({ ...prev, title: event.target.value }))}
                      placeholder="June artist follow-up"
                    />
                  </Field>
                  <Field label="Source">
                    <select
                      value={broadcastForm.sourceType}
                      onChange={(event) => setBroadcastForm((prev) => ({
                        ...prev,
                        sourceType: event.target.value,
                        groupName: "",
                        individualContactId: "",
                      }))}
                    >
                      <option value="individual">Individual contact</option>
                      <option value="group">Group</option>
                    </select>
                  </Field>
                  {broadcastForm.sourceType === "individual" ? (
                    <Field label="Saved contact">
                      <select
                        value={broadcastForm.individualContactId}
                        onChange={(event) => setBroadcastForm((prev) => ({ ...prev, individualContactId: event.target.value }))}
                      >
                        <option value="">Select contact</option>
                        {contactOptions.map((item) => <option key={item.id} value={item.id}>{item.label}</option>)}
                      </select>
                    </Field>
                  ) : (
                    <Field label="Contact group">
                      <select
                        value={broadcastForm.groupName}
                        onChange={(event) => setBroadcastForm((prev) => ({ ...prev, groupName: event.target.value }))}
                      >
                        <option value="">Select group</option>
                        {groups.map((item) => <option key={item.id} value={item.name}>{item.name}</option>)}
                      </select>
                    </Field>
                  )}
                  <Field label="Template">
                    <select
                      value={broadcastForm.templateId}
                      onChange={(event) => setBroadcastForm((prev) => ({ ...prev, templateId: event.target.value }))}
                    >
                      <option value="">Select template</option>
                      {templateOptions.map((item) => <option key={item.id} value={item.id}>{item.label}</option>)}
                    </select>
                  </Field>
                </div>

                {selectedTemplate ? (
                  <div className="crm-panel" style={{ marginTop: 14, padding: 14 }}>
                    <div className="crm-count-grid">
                      <MiniStat label="Category" value={selectedTemplate.category || "—"} />
                      <MiniStat label="Type" value={selectedTemplate.type || "—"} />
                      <MiniStat label="Language" value={selectedTemplate.language || "—"} />
                      <MiniStat label="Variables" value={selectedTemplate.bodyVariableCount || 0} />
                    </div>

                    {bodyVarPairs.length ? (
                      <div style={{ marginTop: 14, display: "grid", gap: 10 }}>
                        <h4 style={{ margin: 0 }}>Template variable</h4>
                        {bodyVarPairs.map((pair, index) => (
                          <div key={pair.key} className="crm-var-row">
                            <input value={pair.key} readOnly />
                            {pair.key === "1" ? (
                              <input
                                value={
                                  broadcastForm.sourceType === "individual"
                                    ? (selectedIndividualContact?.name || selectedIndividualContact?.fullName || "")
                                    : "Auto from each contact name in selected group"
                                }
                                readOnly
                              />
                            ) : (
                              <input
                                value={pair.value}
                                onChange={(event) => setBodyVarPairs((prev) => prev.map((entry, entryIndex) => (
                                  entryIndex === index ? { ...entry, value: event.target.value } : entry
                                )))}
                                placeholder={`Value for {{${pair.key}}}`}
                              />
                            )}
                          </div>
                        ))}
                      </div>
                    ) : (
                      <p className="crm-note" style={{ marginTop: 14 }}>This template does not require body variables.</p>
                    )}

                    <div className="crm-actions" style={{ marginTop: 16 }}>
                      <button type="button" className="crm-btn" onClick={handleStartBroadcast} disabled={busy === "broadcastStart"}>
                        <FaWhatsapp /> {busy === "broadcastStart" ? "Sending..." : "Send Now"}
                      </button>
                    </div>
                  </div>
                ) : (
                  <div className="crm-empty" style={{ marginTop: 14 }}>
                    Select the approved template to continue.
                  </div>
                )}
              </div>

              <div className="crm-panel">
                <h3>Selected data</h3>
                <div className="crm-count-grid">
                  <MiniStat
                    label="Mode"
                    value={broadcastForm.sourceType === "individual" ? "Individual" : "Group"}
                  />
                  <MiniStat
                    label="Contact"
                    value={selectedIndividualContact ? (selectedIndividualContact.fullName || selectedIndividualContact.name || "Selected") : "—"}
                  />
                  <MiniStat
                    label="Phone"
                    value={selectedIndividualContact ? (selectedIndividualContact.phoneE164 || selectedIndividualContact.phoneRaw || "—") : "—"}
                  />
                  <MiniStat
                    label="Group"
                    value={broadcastForm.groupName || "—"}
                  />
                </div>
              </div>
            </div>
          ) : null}

          {activeTab === "reports" ? (
            <div className="crm-two-col">
              <div className="crm-panel">
                <div className="crm-toolbar">
                  <div>
                    <h3 style={{ marginBottom: 6 }}>Campaign History</h3>
                    <p className="crm-panel-sub" style={{ marginBottom: 0 }}>
                      Status here reflects API accepted vs failed, not final delivery/read state.
                    </p>
                  </div>
                  <button type="button" className="crm-btn-secondary" onClick={() => setViewingBroadcast(null)}>
                    <FiRefreshCcw /> Clear Selection
                  </button>
                </div>

                <div className="crm-table-wrap" style={{ marginTop: 14 }}>
                  <table className="crm-table" style={{ minWidth: 860 }}>
                    <thead>
                      <tr>
                        <th>Campaign</th>
                        <th>Total</th>
                        <th>Sent</th>
                        <th>Failed</th>
                        <th>Pending</th>
                        <th>Template</th>
                        <th>Source</th>
                        <th>Date</th>
                      </tr>
                    </thead>
                    <tbody>
                      {broadcasts.length ? broadcasts.map((item) => (
                        <tr key={item.id} onClick={() => setViewingBroadcast(item)} style={{ cursor: "pointer" }}>
                          <td>{item.title || item.templateName || "Untitled"}<small>{item.status || "—"}</small></td>
                          <td>{item.total || 0}</td>
                          <td>{item.sent || 0}</td>
                          <td>{item.failed || 0}</td>
                          <td>{item.pending || 0}</td>
                          <td>{item.templateName || item.templateWid || "—"}</td>
                          <td>{item.sourceType === "group" ? item.groupName || "Group" : "Manual numbers"}</td>
                          <td>{formatDate(item.createdAt)}</td>
                        </tr>
                      )) : (
                        <tr><td colSpan={8}>No campaigns yet.</td></tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </div>

              <div className="crm-panel">
                <div className="crm-toolbar">
                  <div>
                    <h3 style={{ marginBottom: 6 }}>Recipient Status</h3>
                    <p className="crm-panel-sub" style={{ marginBottom: 0 }}>
                      Click a campaign to inspect recipient-level API results and export CSV.
                    </p>
                  </div>
                  {viewingBroadcast ? (
                    <button type="button" className="crm-btn-secondary" onClick={exportRecipientsCsv}>
                      <FaDownload /> Export CSV
                    </button>
                  ) : null}
                </div>

                {viewingBroadcast ? (
                  <>
                    <div className="crm-chip-row" style={{ marginTop: 14 }}>
                      <span className="crm-chip">{viewingBroadcast.title || viewingBroadcast.templateName}</span>
                      <span className="crm-chip">{viewingBroadcast.status || "—"}</span>
                      <span className="crm-chip">{viewingBroadcast.total || 0} recipients</span>
                    </div>
                    <div className="crm-table-wrap" style={{ marginTop: 14 }}>
                      <table className="crm-table" style={{ minWidth: 680 }}>
                        <thead>
                          <tr>
                            <th>Name</th>
                            <th>Phone</th>
                            <th>Status</th>
                            <th>Error</th>
                          </tr>
                        </thead>
                        <tbody>
                          {recipientRows.length ? recipientRows.map((item) => (
                            <tr key={item.id}>
                              <td>{item.name || "—"}</td>
                              <td>{item.phoneE164 || item.phoneRaw || "—"}</td>
                              <td>{item.status || "pending"}</td>
                              <td>{item.error || "—"}</td>
                            </tr>
                          )) : (
                            <tr><td colSpan={4}>Recipients will appear here once selected.</td></tr>
                          )}
                        </tbody>
                      </table>
                    </div>
                  </>
                ) : (
                  <div className="crm-empty" style={{ marginTop: 14 }}>
                    Select a campaign from the left to inspect its recipient results.
                  </div>
                )}
              </div>
            </div>
          ) : null}

          {activeTab === "invite-app-users" ? (
            <InviteAppUsers onNotice={showNotice} />
          ) : null}

        </div>
      </section>

      {progressModal.open ? (
        <div className="crm-progress-backdrop">
          <div className="crm-progress-card">
            <div className="crm-toolbar">
              <div>
                <h3 style={{ margin: 0 }}>{progressModal.data?.title || "Broadcast in progress"}</h3>
                <p className="crm-note" style={{ margin: "8px 0 0" }}>
                  Provider status is based on Authkey API accepted/failed responses.
                </p>
              </div>
              <button
                type="button"
                className="crm-btn-secondary"
                onClick={() => {
                  if (canCloseProgress) {
                    setProgressModal({ open: false, broadcastId: "", data: null });
                  }
                }}
                disabled={!canCloseProgress}
              >
                Close
              </button>
            </div>

            <div className="crm-modal-grid">
              <MiniStat label="Total" value={progressModal.data?.total || 0} />
              <MiniStat label="Sent" value={progressModal.data?.sent || 0} />
              <MiniStat label="Failed" value={progressModal.data?.failed || 0} />
              <MiniStat label="Pending" value={progressModal.data?.pending || 0} />
            </div>

            <div>
              <div className="crm-progress-bar">
                <div className="crm-progress-fill" style={{ width: `${progressPercent}%` }} />
              </div>
              <div className="crm-note" style={{ marginTop: 8 }}>
                {progressPercent}% complete • Current number: {progressModal.data?.currentNumber || "Preparing..."}
              </div>
            </div>

            {Array.isArray(progressModal.data?.errorList) && progressModal.data.errorList.length ? (
              <div className="crm-errors">
                {progressModal.data.errorList.map((item, index) => <div key={`${item}-${index}`}>{item}</div>)}
              </div>
            ) : null}
          </div>
        </div>
      ) : null}
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div className="crm-field">
      <label>{label}</label>
      {children}
    </div>
  );
}

function StatCard({ label, value }) {
  return (
    <div className="crm-stat-card">
      <span className="crm-stat-label">{label}</span>
      <span className="crm-stat-value">{value}</span>
    </div>
  );
}

function MiniStat({ label, value }) {
  return (
    <div className="crm-panel" style={{ padding: 12 }}>
      <div className="crm-note" style={{ marginBottom: 4 }}>{label}</div>
      <strong style={{ fontSize: 22 }}>{value}</strong>
    </div>
  );
}

function formatDate(value) {
  if (!value) return "—";
  if (typeof value?.toDate === "function") {
    return value.toDate().toLocaleString("en-IN");
  }
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "—";
  return date.toLocaleString("en-IN");
}
